import React, { useMemo } from 'react'
import {
  Box,
  Chip,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

export type AccountOperationalStatusProps = {
  /** Flags “positivas” */
  canCreateProducts: boolean
  canSell: boolean
  canWithdraw: boolean

  /** Flag “restritiva” (quando true, impede 100%) */
  requirePreValidation: boolean

  /** Opcional: título do bloco */
  title?: string

  /** Opcional: exibir % */
  showPercent?: boolean
}

/**
 * Indicador administrativo de “Prontidão/Status Operacional” da conta.
 * - 0%..100% baseado nas permissões ativas.
 * - 100% somente se as 3 permissões estiverem ativas e NÃO houver restrição de validação prévia.
 *
 * Uso:
 * <AccountOperationalStatus
 *   canCreateProducts={...}
 *   canSell={...}
 *   canWithdraw={...}
 *   requirePreValidation={...}
 * />
 */
export function AccountOperationalStatus({
  canCreateProducts,
  canSell,
  canWithdraw,
  requirePreValidation,
  title = 'Status operacional da conta',
  showPercent = true,
}: AccountOperationalStatusProps) {
  const { percent, label, chipVariant, tooltip } = useMemo(() => {
    // pesos (somam 100)
    const wCreate = 33
    const wSell = 33
    const wWithdraw = 34

    let p = 0
    if (canCreateProducts) p += wCreate
    if (canSell) p += wSell
    if (canWithdraw) p += wWithdraw

    // regra: se exigir validação prévia, não permitir 100% (limita a 66%)
    // (mantém o sentido: conta pode vender/sacar, mas com restrição de publicação)
    if (requirePreValidation && p > 66) p = 66

    // label por faixa
    let lbl = 'Bloqueada'
    let variant: 'default' | 'outlined' = 'outlined'

    if (p === 0) {
      lbl = 'Bloqueada'
      variant = 'outlined'
    } else if (p > 0 && p < 66) {
      lbl = 'Parcialmente habilitada'
      variant = 'outlined'
    } else if (p === 66 && requirePreValidation) {
      lbl = 'Habilitada com restrição'
      variant = 'outlined'
    } else if (p >= 66 && p < 100) {
      lbl = 'Habilitada'
      variant = 'default'
    } else if (p === 100) {
      lbl = 'Totalmente habilitada'
      variant = 'default'
    }

    const tt =
      'Indicador administrativo de prontidão operacional. Considera permissões ativas (cadastrar, vender, sacar) e restrições aplicadas. A conta só atinge 100% quando todas as permissões estão ativas e não há exigência de validação prévia para novos produtos.'

    return {
      percent: p,
      label: lbl,
      chipVariant: variant,
      tooltip: tt,
    }
  }, [canCreateProducts, canSell, canWithdraw, requirePreValidation])

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {title}
            </Typography>

            <Tooltip title={tooltip} arrow>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: 'help',
                  color: 'text.secondary',
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </Box>
            </Tooltip>
          </Stack>

          <Chip
            size="small"
            label={label}
            variant={chipVariant === 'default' ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700 }}
          />
        </Stack>

        <Box>
          <LinearProgress
            variant="determinate"
            value={percent}
            sx={{
              height: 10,
              borderRadius: 999,
              bgcolor: 'action.hover',
            }}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ mt: 0.75 }}
          >
            <Typography variant="caption" color="text.secondary">
              {requirePreValidation
                ? 'Restrição ativa: validação prévia para novos produtos'
                : 'Sem restrições de validação prévia'}
            </Typography>

            {showPercent && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700 }}
              >
                {percent}%
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  )
}
